import { AlertColor } from '@mui/material';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';
import strings from '../../../../../../../common/Translation/Translate';
import Notification from '../../../../../../../components/Notification';
import { PricePackageCards } from '../PricePackageCards';
import {
  createEmptyPricePackageRow,
  DropdownOption,
  getPricePackageFieldErrors,
  PricePackageFieldErrors,
  PricePackageRow,
} from '../priceFormUtils';

type AddEditPricePackageProps = {
  editedData: PricePackageRow[];
  setData: Dispatch<SetStateAction<PricePackageRow[]>>;
  dropdownValue: any;
};

export const AddEditPricePackage: React.FC<AddEditPricePackageProps> = ({
  editedData = [],
  setData,
  dropdownValue,
}) => {
  const [errors, setErrors] = useState<Record<number, PricePackageFieldErrors>>({});
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });

  const packageOptions: DropdownOption[] = useMemo(
    () => dropdownValue?.pricePackageId ?? dropdownValue?.pricePackages ?? [],
    [dropdownValue],
  );

  const closeNotification = (value: boolean) => setMessage(prev => ({ ...prev, display: value }));

  const setErrorForIndex = useCallback((index: number, row: PricePackageRow) => {
    const fieldErrors = getPricePackageFieldErrors(row);
    const hasErrors = Boolean(fieldErrors.pricePackageId || fieldErrors.pricePerPerson);
    setErrors(prev => {
      const next = { ...prev };
      if (hasErrors) next[index] = fieldErrors;
      else delete next[index];
      return next;
    });
    return fieldErrors.pricePackageId ?? fieldErrors.pricePerPerson ?? '';
  }, []);

  const patchRow = useCallback(
    (index: number, patch: Partial<PricePackageRow>) => {
      setData(prev => {
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        setErrorForIndex(index, next[index]);
        return next;
      });
    },
    [setErrorForIndex],
  );

  const handleChange = (index: number, field: keyof PricePackageRow, value: any) => {
    patchRow(index, { [field]: value });
  };

  const handlePackageSelect = (index: number, option: DropdownOption | undefined) => {
    if (!option) {
      patchRow(index, { pricePackageId: '', pricePackageName: '' });
      return;
    }
    patchRow(index, { pricePackageId: option.value, pricePackageName: option.label });
  };

  const handleBlur = (index: number) => {
    const row = editedData[index];
    if (!row) return;
    const error = setErrorForIndex(index, row);
    if (error) {
      setMessage({ display: true, severity: 'error', message: error });
    }
  };

  const handleDelete = (index: number) => {
    setData(editedData.filter((_, i) => i !== index));
    setErrors(prev => {
      const next: Record<number, PricePackageFieldErrors> = {};
      Object.entries(prev).forEach(([key, value]) => {
        const i = Number(key);
        if (i < index) next[i] = value;
        else if (i > index) next[i - 1] = value;
      });
      return next;
    });
  };

  const handleAdd = () => {
    setData([...editedData, createEmptyPricePackageRow()]);
  };

  return (
    <>
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={closeNotification}
          autoHideDuration={2000}
        />
      )}
      <PricePackageCards
        rows={editedData}
        packageOptions={packageOptions}
        errors={errors}
        onChange={handleChange}
        onPackageSelect={handlePackageSelect}
        onBlur={handleBlur}
        onDelete={handleDelete}
        onAdd={handleAdd}
      />
    </>
  );
};
